/**
 * Vanilla confirm-modal module — replaces the `data-confirm-modal` npm package.
 *
 * Two activation modes:
 *
 *   1. **Declarative** (Rails UJS):
 *      Elements with `data-confirm="Are you sure?"` get a modal confirmation
 *      dialog instead of the browser's native `window.confirm()`.
 *      Works with `data-method`, `data-remote`, form submits, etc.
 *
 *   2. **Programmatic**:
 *      `dataConfirmModal.confirm({ title, text, onConfirm, onCancel })`
 *
 * Uses `window.__modal` (from modal.ts) for show/hide.
 *
 * Exposes `window.dataConfirmModal` with:
 *   - `setDefaults({ title, commit, cancel, ... })`
 *   - `restoreDefaults()`
 *   - `confirm({ title, text, commit, cancel, onConfirm, onCancel })`
 */

// Guard against double-initialisation
if ((window as any).__confirmInitialised) {
  // already running — bail out
} else {
(window as any).__confirmInitialised = true;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConfirmDefaults {
  title: string;
  commit: string;
  commitClass: string;
  cancel: string;
  cancelClass: string;
  focus: string;
  zIndex: number;
}

interface ConfirmOptions {
  title?: string;
  text?: string;
  commit?: string;
  commitClass?: string;
  cancel?: string;
  cancelClass?: string;
  focus?: string;
  method?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  verify?: boolean | string;
  verifyRegexp?: string;
  verifyLabel?: string;
  verifyRegexpCaseInsensitive?: boolean;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_SETTINGS: ConfirmDefaults = {
  title: "Are you sure?",
  commit: "Confirm",
  commitClass: "btn-danger",
  cancel: "Cancel",
  cancelClass: "btn-default",
  focus: "commit",
  zIndex: 1050,
};

let settings: ConfirmDefaults = { ...DEFAULT_SETTINGS };

// ---------------------------------------------------------------------------
// Modal builder
// ---------------------------------------------------------------------------

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function buildModal(options: ConfirmOptions): HTMLElement {
  const id = "confirm-modal-" + Math.random().toString(36).slice(2);
  const title = options.title || settings.title;
  const commitText = options.commit || settings.commit;
  const commitClass = options.commitClass || settings.commitClass;
  const cancelText = options.cancel || settings.cancel;
  const cancelClass = options.cancelClass || settings.cancelClass;

  const modal = document.createElement("div");
  modal.id = id;
  modal.className = "modal fade";
  modal.tabIndex = -1;
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-labelledby", id + "Label");
  modal.setAttribute("aria-hidden", "true");
  modal.style.zIndex = String(settings.zIndex);

  modal.innerHTML = [
    '<div class="modal-dialog" role="document">',
    '  <div class="modal-content">',
    '    <div class="modal-header">',
    '      <button type="button" class="close" data-modal-dismiss aria-label="Close">&times;</button>',
    `      <h5 id="${id}Label" class="modal-title">${escapeHtml(title)}</h5>`,
    "    </div>",
    '    <div class="modal-body"></div>',
    '    <div class="modal-footer">',
    `      <button type="button" class="btn cancel ${escapeHtml(cancelClass)}" data-modal-dismiss>${escapeHtml(cancelText)}</button>`,
    `      <button type="button" class="btn commit ${escapeHtml(commitClass)}">${escapeHtml(commitText)}</button>`,
    "    </div>",
    "  </div>",
    "</div>",
  ].join("\n");

  // Body text — split double newlines into paragraphs
  const body = modal.querySelector(".modal-body")!;
  const text = options.text || "";
  if (text) {
    text.split(/\n{2}/).forEach((piece) => {
      const p = document.createElement("p");
      // data-confirm values come from ERB templates (developer-authored),
      // not user input. Using innerHTML to preserve intentional HTML formatting.
      p.innerHTML = piece;
      body.appendChild(p);
    });
  }

  // Commit button
  const commitBtn = modal.querySelector<HTMLElement>(".commit")!;

  // Verification input (type-to-confirm)
  if (options.verify || options.verifyRegexp) {
    commitBtn.setAttribute("disabled", "");

    let isMatch: (input: string) => boolean;
    if (options.verifyRegexp) {
      const flags = options.verifyRegexpCaseInsensitive ? "i" : "";
      const re = new RegExp(options.verifyRegexp, flags);
      isMatch = (input) => re.test(input);
    } else {
      const verifyText = typeof options.verify === "string" ? options.verify : "";
      isMatch = (input) => input === verifyText;
    }

    const verification = document.createElement("input");
    verification.type = "text";
    verification.className = "form-control";
    verification.addEventListener("keyup", () => {
      if (isMatch(verification.value)) {
        commitBtn.removeAttribute("disabled");
      } else {
        commitBtn.setAttribute("disabled", "");
      }
    });

    if (options.verifyLabel) {
      const label = document.createElement("p");
      label.textContent = options.verifyLabel;
      body.appendChild(label);
    }
    body.appendChild(verification);

    // Focus verification input when shown
    modal.addEventListener("shown.bs.modal", () => verification.focus());
    // Reset on close
    modal.addEventListener("hidden.bs.modal", () => {
      verification.value = "";
      commitBtn.setAttribute("disabled", "");
    });
  }

  // Focus management (skip when verify input handles its own focus)
  if (!options.verify && !options.verifyRegexp) {
    const focusTarget = options.focus
      || (options.method === "delete" ? "cancel" : settings.focus);
    modal.addEventListener("shown.bs.modal", () => {
      const btn = modal.querySelector<HTMLElement>(`.${focusTarget}`);
      btn?.focus();
    });
  }

  document.body.appendChild(modal);
  return modal;
}

// ---------------------------------------------------------------------------
// Programmatic API: dataConfirmModal.confirm()
// ---------------------------------------------------------------------------

function confirmProgrammatic(options: ConfirmOptions): void {
  const modal = buildModal(options);
  const modalAPI = (window as any).__modal;

  const commitBtn = modal.querySelector<HTMLElement>(".commit")!;

  commitBtn.addEventListener("click", () => {
    if (options.onConfirm) options.onConfirm();
    modalAPI.hide(modal);
  });

  // Cancel callback — both dismiss buttons and backdrop close trigger hidden event
  const cancelBtn = modal.querySelector<HTMLElement>(".cancel");
  if (cancelBtn && options.onCancel) {
    cancelBtn.addEventListener("click", () => {
      options.onCancel!();
    });
  }

  // Remove modal from DOM after hidden
  modal.addEventListener("hidden.bs.modal", () => {
    modal.remove();
  });

  modalAPI.show(modal);
}

// ---------------------------------------------------------------------------
// Rails UJS integration
// ---------------------------------------------------------------------------
// Rails UJS fires a `confirm` event on elements with `data-confirm`.
// If the handler returns false, the action is cancelled.
// data-confirm-modal's trick: first call returns false (shows modal).
// Commit button re-clicks the original element; second call returns true
// because window.confirm is temporarily overridden.

const CONFIRM_SELECTORS = "a[data-confirm], button[data-confirm], input[type=submit][data-confirm]";
const nativeConfirm = window.confirm;

// Cache for element→modal mapping (avoids rebuilding on every click)
const elementModals = new WeakMap<HTMLElement, HTMLElement>();

function getOrBuildElementModal(element: HTMLElement): HTMLElement {
  let modal = elementModals.get(element);
  if (modal && modal.isConnected) return modal;

  const options: ConfirmOptions = {
    title: element.getAttribute("data-title")
      || element.getAttribute("title")
      || element.getAttribute("data-original-title")
      || undefined,
    text: element.getAttribute("data-confirm") || undefined,
    focus: element.getAttribute("data-focus") || undefined,
    method: element.getAttribute("data-method") || undefined,
    commit: element.getAttribute("data-commit") || undefined,
    commitClass: element.getAttribute("data-commit-class") || undefined,
    cancel: element.getAttribute("data-cancel") || undefined,
    cancelClass: element.getAttribute("data-cancel-class") || undefined,
  };

  modal = buildModal(options);
  const modalAPI = (window as any).__modal;

  // Commit: re-click the original element with window.confirm overridden
  const commitBtn = modal.querySelector<HTMLElement>(".commit")!;
  commitBtn.addEventListener("click", () => {
    element.click();
    modalAPI.hide(modal!);
  });

  elementModals.set(element, modal);
  return modal;
}

// Listen for Rails UJS `confirm` event
document.addEventListener("confirm", (e: Event) => {
  const element = e.target as HTMLElement;
  if (!element.matches(CONFIRM_SELECTORS)) return;

  const modal = getOrBuildElementModal(element);
  const modalAPI = (window as any).__modal;

  // If modal is currently visible, the user has clicked "commit"
  // and the element was re-clicked. Override window.confirm to return true.
  if (modal.classList.contains("in")) {
    window.confirm = () => true;
    // Reset after the modal is hidden
    const resetConfirm = () => {
      window.confirm = nativeConfirm;
      modal.removeEventListener("hidden.bs.modal", resetConfirm);
    };
    modal.addEventListener("hidden.bs.modal", resetConfirm);
    return; // allow the action to proceed
  }

  // First call — show the modal and cancel the action
  modalAPI.show(modal);

  // Return false to cancel Rails UJS action via stopImmediatePropagation
  e.stopImmediatePropagation();
  e.preventDefault();
}, true); // capture phase to run before jQuery UJS confirm handler

// Also prevent the default jQuery UJS confirm behavior by overriding $.rails.confirm
// when our module is active. jQuery UJS calls $.rails.allowAction → $.rails.confirm.
// We need to hook into the jQuery event delegation system.
function patchJQueryUJSConfirm(): void {
  const $ = (window as any).jQuery;
  if (!$ || !$.rails) return;

  $.rails.allowAction = function (element: any) {
    const message = element.data("confirm");
    if (!message) return true;

    const el: HTMLElement | undefined = element.get(0);
    if (!el) return true;
    const modal = getOrBuildElementModal(el);
    const modalAPI = (window as any).__modal;

    // If modal is visible, user clicked commit — allow action
    if (modal.classList.contains("in")) {
      return true;
    }

    // Show modal and deny action
    modalAPI.show(modal);
    return false;
  };
}

// ---------------------------------------------------------------------------
// Global API
// ---------------------------------------------------------------------------

const dataConfirmModalAPI = {
  setDefaults(newSettings: Partial<ConfirmDefaults>): void {
    Object.assign(settings, newSettings);
  },
  restoreDefaults(): void {
    settings = { ...DEFAULT_SETTINGS };
  },
  confirm: confirmProgrammatic,
};

(window as any).dataConfirmModal = dataConfirmModalAPI;

// Patch jQuery UJS if available now, or after DOM ready
if ((window as any).jQuery?.rails) {
  patchJQueryUJSConfirm();
} else {
  document.addEventListener("DOMContentLoaded", patchJQueryUJSConfirm);
}

} // end of double-init guard
